/**
 * Client-side photo shrink for Kit Swap (B-45). The server refuses anything
 * over 300 KB; we aim for longest side 1024px, JPEG, ~150 KB or less, so a
 * phone photo (3–8 MB) uploads in a blink on pitch-side 4G.
 *
 * Browsers apply EXIF orientation when decoding into an <img> (CSS
 * image-orientation: from-image is the default), so portrait phone shots
 * stay upright. Drawing to a canvas also strips EXIF (incl. GPS) — the
 * uploaded file carries no location.
 */
export const TARGET_BYTES = 150 * 1024;
export const SERVER_MAX_BYTES = 300 * 1024;

const loadImage = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('That file isn\'t a photo we can read.')); };
  img.src = url;
});

const toBlob = (canvas, quality) => new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));

export async function shrinkPhoto(file) {
  if (!file || !/^image\//.test(file.type || 'image/')) throw new Error('Choose a photo.');
  const img = await loadImage(file);
  let longest = 1024;
  for (let pass = 0; pass < 4; pass++) {
    const scale = Math.min(1, longest / Math.max(img.naturalWidth, img.naturalHeight));
    const w = Math.max(1, Math.round(img.naturalWidth * scale));
    const h = Math.max(1, Math.round(img.naturalHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff'; // transparent PNGs → white, not black
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    for (const q of [0.82, 0.72, 0.62, 0.52]) {
      const blob = await toBlob(canvas, q);
      if (blob && blob.size <= TARGET_BYTES) return blob;
      if (blob && q === 0.52 && blob.size <= SERVER_MAX_BYTES && pass >= 1) return blob;
    }
    longest = Math.round(longest * 0.8);
  }
  throw new Error('We couldn\'t make that photo small enough — try another one.');
}
